/**
 * F2.06b — Ranking de variantes candidatas por coherencia espacial.
 *
 * Dadas las variantes generadas por F2.06a, las puntúa según proximidad
 * al centroide del municipio (CENTROIDES_MUNICIPIOS) y pertenencia al
 * rango UTM válido de Andalucía.
 *
 * Función pura: no realiza llamadas a APIs. Usa datos estáticos de
 * centroides municipales y rangos UTM de constants.ts.
 */

import type { CoordinateVariant } from './coordinateVariants';
import { CENTROIDES_MUNICIPIOS } from './municipiosCentroides';
import { RANGOS_ANDALUCIA } from './coordinate/constants';

// ============================================================================
// TIPOS
// ============================================================================

export interface RankedCoordinateVariant extends CoordinateVariant {
  /** Puntuación 0-100 de coherencia espacial */
  score: number;
  /** Distancia euclidiana al centroide municipal en metros */
  distanceToCentroid: number;
  /** ¿Está dentro de los rangos UTM de Andalucía? */
  inAndaluciaRange: boolean;
}

/** Eje de la coordenada que se está corrigiendo */
export type CoordinateAxis = 'x' | 'y';

// ============================================================================
// CONSTANTES
// ============================================================================

/** Distancia máxima razonable al centroide (metros) — más allá, score = 0 */
const MAX_DISTANCE_M = 50_000;

/** Distancia ideal: dentro del casco urbano */
const IDEAL_DISTANCE_M = 5_000;

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Puntúa y ordena variantes candidatas por coherencia espacial.
 *
 * Para cada variante, reconstruye el punto UTM completo combinando
 * el valor de la variante con la coordenada conocida del otro eje,
 * y calcula la distancia al centroide del municipio.
 *
 * @param variants - Variantes generadas por generateCoordinateVariants()
 * @param pairedCoordinate - Valor conocido del otro eje (X si corregimos Y, Y si corregimos X)
 * @param axis - Eje que estamos corrigiendo: 'x' o 'y'
 * @param ine5 - Código INE de 5 dígitos del municipio
 * @returns Variantes rankeadas, ordenadas de mayor a menor score (mejor primero)
 */
export function rankCoordinateVariants(
  variants: CoordinateVariant[],
  pairedCoordinate: number,
  axis: CoordinateAxis,
  ine5: string,
): RankedCoordinateVariant[] {
  if (variants.length === 0) return [];

  const centroid = CENTROIDES_MUNICIPIOS[ine5];
  if (!centroid) return [];

  const { UTM } = RANGOS_ANDALUCIA;

  const ranked: RankedCoordinateVariant[] = variants.map((variant) => {
    const x = axis === 'x' ? variant.value : pairedCoordinate;
    const y = axis === 'y' ? variant.value : pairedCoordinate;

    const inAndaluciaRange =
      x >= UTM.X_MIN && x <= UTM.X_MAX &&
      y >= UTM.Y_MIN && y <= UTM.Y_MAX;

    const dx = x - centroid.x;
    const dy = y - centroid.y;
    const distanceToCentroid = Math.sqrt(dx * dx + dy * dy);

    const score = calculateScore(distanceToCentroid, inAndaluciaRange);

    return {
      ...variant,
      score,
      distanceToCentroid: Math.round(distanceToCentroid),
      inAndaluciaRange,
    };
  });

  // Ordenar: mayor score primero; a igual score, menor distancia
  ranked.sort((a, b) => b.score - a.score || a.distanceToCentroid - b.distanceToCentroid);

  return ranked;
}

// ============================================================================
// SCORING
// ============================================================================

/**
 * Calcula score 0-100 según distancia al centroide.
 *
 * - Fuera de Andalucía → 0
 * - ≤ 5 km → 100
 * - 5-50 km → interpolación lineal 100→0
 * - > 50 km → 0
 */
function calculateScore(distanceM: number, inRange: boolean): number {
  if (!inRange) return 0;
  if (distanceM <= IDEAL_DISTANCE_M) return 100;
  if (distanceM >= MAX_DISTANCE_M) return 0;

  // Interpolación lineal entre IDEAL y MAX
  const ratio = (distanceM - IDEAL_DISTANCE_M) / (MAX_DISTANCE_M - IDEAL_DISTANCE_M);
  return Math.round(100 * (1 - ratio));
}
